import React, { useState, useEffect } from 'react';
import { Table, Input, Select, Button, Space, Tag, Typography, Card, message, Avatar, Modal, Form } from 'antd';
import { SearchOutlined, ReloadOutlined, UserOutlined, ShopOutlined, PlusOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';

// New Theme Colors
const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

const { Title } = Typography;
const { Option } = Select;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  .ant-typography {
    color: ${COLORS.textPrimary} !important;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  
  h2 {
    margin-bottom: 8px !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  p {
    color: ${COLORS.textSecondary};
    margin: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  border-radius: 12px !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  label {
    color: ${COLORS.textSecondary} !important;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 6px !important;
    display: block;
  }
  
  .ant-input,
  .ant-select-selector {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    border-radius: 8px !important;
    height: 40px !important;
    display: flex;
    align-items: center;
    
    &:hover,
    &:focus {
      border-color: ${COLORS.accent} !important;
    }
  }
  
  .ant-input::placeholder {
    color: #4b5563 !important;
  }
  
  .ant-btn {
    height: 40px;
    border-radius: 8px;
    font-weight: 500;
    border: 1px solid ${COLORS.border};
    background: ${COLORS.bgPrimary};
    color: ${COLORS.textPrimary};
    
    &:hover {
      border-color: ${COLORS.accent};
      color: ${COLORS.accent};
    }
  }
`;

const StyledTableCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  border-radius: 12px !important;
  overflow: hidden;
  
  .ant-card-body {
    padding: 0;
  }
  
  @media (max-width: 768px) {
    .ant-card-body {
      padding: 0 !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    gap: 12px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterItem = styled.div`
  flex: 1;
  min-width: 200px;
  
  @media (max-width: 768px) {
    min-width: 100%;
    flex: none;
  }
`;

const Users: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [administratorRoles, setAdministratorRoles] = useState<string[]>([]);
  const [administratorPermissions, setAdministratorPermissions] = useState<string[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  // Get userType from URL path (e.g., /kivu/users/Administrators or /kivu/users/Merchants)
  const pathParts = location.pathname.split('/');
  const userTypeFromPath = pathParts[pathParts.length - 1]; // Get last part of path
  const userType = userTypeFromPath === 'Merchants' ? 'Merchant' : 'Administrator';

  // Get filters from URL query params
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const merchantCompanyId = searchParams.get('merchantCompanyId') || '';
  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchUsers();
    fetchEnums();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, search, status, merchantCompanyId, pageId, perPage]);

  const fetchEnums = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.PUBLIC_GET_ENUMS);
      if (response.data?.data) {
        setAdministratorRoles(response.data.data.administratorRoles || []);
        setAdministratorPermissions(response.data.data.administratorPermissions || []);
      }
    } catch (error) {
      console.error('Failed to fetch enums:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params: any = {
        userType,
        pageId,
        perPage,
      };

      if (search) params.search = search;
      if (status) params.status = status;
      if (merchantCompanyId) params.merchantCompanyId = merchantCompanyId;

      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_USERS, { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setUsers(data.itemsList || []);
        setPagination({
          current: data.currentPage || pageId,
          pageSize: data.perPage || perPage,
          total: data.totalItems || 0,
        });
      } else {
        message.error('Failed to fetch users');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('pageId', '1'); // Reset to first page on filter change
    setSearchParams(newParams);
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      ACTIVE: { color: '#10B981', text: 'Active' },
      PENDING: { color: '#F59E0B', text: 'Pending' },
      SUSPENDED: { color: '#EF4444', text: 'Suspended' },
      DELETED: { color: '#6B7280', text: 'Deleted' },
    };
    const config = statusConfig[status] || { color: '#6B7280', text: status };
    return (
      <Tag 
        color="transparent" 
        style={{ 
          color: config.color, 
          border: `1px solid ${config.color}40`,
          background: `${config.color}10`,
          borderRadius: '6px',
          padding: '0 8px'
        }}
      >
        {config.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'Avatar',
      key: 'avatar',
      width: 80,
      render: (_: any, record: any) => (
        <Avatar
          src={record.portraitUrl ? `data:image/jpeg;base64,${record.portraitUrl}` : undefined}
          icon={<UserOutlined />}
          size={40}
          style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
        />
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: false,
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Country',
      dataIndex: 'countryCode',
      key: 'countryCode',
      render: (code: string) => code ? (
        <Space>
          <ReactCountryFlag countryCode={code} svg style={{ width: '1.5em', height: '1.5em' }} />
          {code}
        </Space>
      ) : '-',
    },
    ...(userType === 'Merchant' ? [{
      title: 'Company',
      key: 'company',
      render: (_: any, record: any) => {
        if (record.company) {
          return (
            <Space>
              {record.company.logoUrl && (
                <Avatar
                  src={record.company.logoUrl}
                  icon={<ShopOutlined />}
                  size={24}
                  style={{ backgroundColor: COLORS.bgSecondary }}
                />
              )}
              <span>{record.company.name}</span>
            </Space>
          );
        }
        return '-';
      },
    }] : []),
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            onClick={() => navigate(`/kivu/users/${record.id}?userType=${userType}`)}
            style={{ color: COLORS.accent, padding: 0 }}
          >
            View Details
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <div>
          <Title level={2}>
            {userType === 'Administrator' ? (
              <><UserOutlined /> Administrator Users</>
            ) : (
              <><ShopOutlined /> Merchant Users</>
            )}
          </Title>
          <p>Manage and view all registered {userType === 'Administrator' ? 'administrator' : 'merchant'} users</p>
        </div>
        {userType === 'Administrator' && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Add Admin
          </Button>
        )}
      </HeaderContainer>

      <FiltersContainer>
        <FiltersRow>
          <FilterItem>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Search
            </label>
            <Input
              placeholder="Search by email, name..."
              prefix={<SearchOutlined />}
              defaultValue={search}
              onBlur={(e) => {
                const newParams = new URLSearchParams(searchParams);
                if (e.target.value) {
                  newParams.set('search', e.target.value);
                } else {
                  newParams.delete('search');
                }
                newParams.set('pageId', '1');
                setSearchParams(newParams);
              }}
              onPressEnter={(e) => {
                const target = e.target as HTMLInputElement;
                const newParams = new URLSearchParams(searchParams);
                if (target.value) {
                  newParams.set('search', target.value);
                } else {
                  newParams.delete('search');
                }
                newParams.set('pageId', '1');
                setSearchParams(newParams);
              }}
              allowClear
            />
          </FilterItem>

          <FilterItem>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Status
            </label>
            <Select
              value={status}
              onChange={(value) => handleFilterChange('status', value)}
              onBlur={() => {
                // Trigger filter update on blur
                const newParams = new URLSearchParams(searchParams);
                newParams.set('pageId', '1');
                setSearchParams(newParams);
              }}
              style={{ width: '100%' }}
              allowClear
              placeholder="All Statuses"
            >
              <Option value="ACTIVE">Active</Option>
              <Option value="PENDING">Pending</Option>
              <Option value="SUSPENDED">Suspended</Option>
              <Option value="DELETED">Deleted</Option>
            </Select>
          </FilterItem>

          {userType === 'Merchant' && (
            <FilterItem>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Merchant Company ID
              </label>
              <Input
                placeholder="Filter by company ID"
                defaultValue={merchantCompanyId}
                onBlur={(e) => {
                  const newParams = new URLSearchParams(searchParams);
                  if (e.target.value) {
                    newParams.set('merchantCompanyId', e.target.value);
                  } else {
                    newParams.delete('merchantCompanyId');
                  }
                  newParams.set('pageId', '1');
                  setSearchParams(newParams);
                }}
                onPressEnter={(e) => {
                  const target = e.target as HTMLInputElement;
                  const newParams = new URLSearchParams(searchParams);
                  if (target.value) {
                    newParams.set('merchantCompanyId', target.value);
                  } else {
                    newParams.delete('merchantCompanyId');
                  }
                  newParams.set('pageId', '1');
                  setSearchParams(newParams);
                }}
                allowClear
              />
            </FilterItem>
          )}

          <Button
            icon={<ReloadOutlined />}
            onClick={fetchUsers}
            loading={loading}
          >
            Refresh
          </Button>
        </FiltersRow>
      </FiltersContainer>

      <StyledTableCard>
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total) => <span style={{ color: '#94A3B8' }}>Total {total} users</span>,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            className="dark-table"
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </StyledTableCard>

      {userType === 'Administrator' && (
        <Modal
          title="Create Administrator"
          open={modalVisible}
          onOk={async () => {
            try {
              setCreatingAdmin(true);
              const values = await form.validateFields();
              await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_ADMIN_ACCOUNT, {
                email: values.email,
                role: values.role || 'ADMIN',
                permissions: values.permissions || [],
              });
              message.success('Administrator created successfully. They will receive an email to complete their registration.');
              setModalVisible(false);
              form.resetFields();
              fetchUsers();
            } catch (error: any) {
              message.error(error.response?.data?.message || 'Failed to create administrator');
            } finally {
              setCreatingAdmin(false);
            }
          }}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Create"
          confirmLoading={creatingAdmin}
          cancelText="Cancel"
          width={600}
          bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
          style={{ top: 20 }}
        >
          <Form
            form={form}
            layout="vertical"
            style={{ marginTop: 20 }}
          >
            <div style={{ 
              padding: '12px 16px', 
              background: COLORS.bgPrimary, 
              border: `1px solid ${COLORS.border}`, 
              borderRadius: '8px',
              marginBottom: 20,
              color: COLORS.textSecondary,
              fontSize: '14px'
            }}>
              Admin will get an email to complete their registration and set their own password, first name, last name, and phone number.
            </div>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, type: 'email', message: 'Please enter valid email' }]}
            >
              <Input placeholder="admin@example.com" />
            </Form.Item>
            <Form.Item
              name="role"
              label="Role"
              initialValue="ADMIN"
              rules={[{ required: true, message: 'Please select a role' }]}
            >
              <Select placeholder="Select Role">
                  {/* ensure selected text is visible on dark theme */}
                {administratorRoles.map((role) => (
                  <Select.Option key={role} value={role}>
                    {role.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="permissions"
              label="Permissions"
            >
              <Select
                className="select-with-text"
                mode="multiple"
                placeholder="Select Permissions (optional)"
                allowClear
              >
                {administratorPermissions.map((permission) => (
                  <Select.Option key={permission} value={permission}>
                    {permission.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </PageContainer>
  );
};

export default Users;
